import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '@/context/AuthContext';
import '@/styles/SipaStyles.scss';
import * as Sentry from '@sentry/react';

/**
 * SipaWorkflowConfigPage Component
 * 
 * Allows administrators to configure which system roles are authorized to:
 * - Perform primary (initial) sign-off on SIPA reports
 * - Perform secondary (final) sign-off (with OR logic: can also do primary in someone's absence)
 * - Override validation failures and submit a report even with outstanding issues
 * 
 * Works with role IDs internally; displays role names for the user.
 */
const SipaWorkflowConfigPage = () => {
    const { apiFetch } = useAuth();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [availableRoles, setAvailableRoles] = useState([]);
    const [primaryRoleIds, setPrimaryRoleIds] = useState([]);
    const [secondaryRoleIds, setSecondaryRoleIds] = useState([]);
    const [overrideRoleIds, setOverrideRoleIds] = useState([]);
    const [lastUpdated, setLastUpdated] = useState(null);

    const parseIdCSV = (csv) => {
        if (!csv) return [];
        return csv.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n) && n > 0);
    };

    const toIdCSV = (arr) => arr.join(', ');

    const fetchConfig = useCallback(async () => {
        setLoading(true);
        try {
            const [configRes, rolesRes] = await Promise.all([
                apiFetch('/api/sipa/workflow-config'),
                apiFetch('/api/sipa/workflow-config/roles')
            ]);

            const configData = await configRes.json().catch(() => null);
            const rolesData = await rolesRes.json().catch(() => null);

            if (configData?.success && configData.data) {
                setPrimaryRoleIds(parseIdCSV(configData.data.primarySignoffRoleIds || configData.data.PrimarySignoffRoleIds));
                setSecondaryRoleIds(parseIdCSV(configData.data.secondarySignoffRoleIds || configData.data.SecondarySignoffRoleIds));
                setOverrideRoleIds(parseIdCSV(configData.data.validationOverrideRoleIds || configData.data.ValidationOverrideRoleIds));
                setLastUpdated(configData.data.updatedUtc || configData.data.UpdatedUtc);
            }

            if (rolesData?.success && Array.isArray(rolesData.data)) {
                setAvailableRoles(rolesData.data.map(r => ({
                    roleID: r.roleID ?? r.RoleID,
                    roleName: r.roleName ?? r.RoleName
                })).filter(r => r.roleID && r.roleName));
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Failed to load workflow configuration');
        } finally {
            setLoading(false);
        }
    }, [apiFetch]);

    useEffect(() => { fetchConfig(); }, [fetchConfig]);

    // Track changes to avoid auto-saving on mount.
    // Counter: 0 = not loaded, 1 = initial load applied (skip), 2+ = user changed.
    const configChangeCountRef = useRef(0);

    // Auto-save when role selections change after initial load
    useEffect(() => {
        if (loading || availableRoles.length === 0) return;
        configChangeCountRef.current += 1;
        // Skip the first change (initial API load populating state)
        if (configChangeCountRef.current <= 1) return;

        const timer = setTimeout(() => {
            handleSave();
        }, 400);
        return () => clearTimeout(timer);
    }, [primaryRoleIds, secondaryRoleIds, overrideRoleIds]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await apiFetch('/api/sipa/workflow-config', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    primarySignoffRoleIds: toIdCSV(primaryRoleIds),
                    secondarySignoffRoleIds: toIdCSV(secondaryRoleIds),
                    validationOverrideRoleIds: toIdCSV(overrideRoleIds)
                })
            });
            const data = await res.json().catch(() => null);
            if (res.ok && data?.success) {
                toast.success(data.message || 'Configuration saved');
                setLastUpdated(data.data?.updatedUtc || data.data?.UpdatedUtc || new Date().toISOString());
            } else {
                toast.error(data?.message || 'Failed to save configuration');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Failed to save configuration');
        } finally {
            setSaving(false);
        }
    };

    const toggleRoleId = (list, setList, roleId) => {
        if (list.includes(roleId)) {
            setList(list.filter(id => id !== roleId));
        } else {
            setList([...list, roleId]);
        }
    };

    const getRoleName = (roleId) => {
        const role = availableRoles.find(r => r.roleID === roleId);
        return role ? role.roleName : `Unknown (ID: ${roleId})`;
    };

    const RoleSelector = ({ label, description, selectedIds, setSelectedIds, color }) => {
        const availableIds = availableRoles.map(r => r.roleID);
        const staleIds = selectedIds.filter(id => !availableIds.includes(id));
        const allIds = [...availableIds, ...staleIds];

        return (
            <div className="config-section" style={{ marginBottom: '24px' }}>
                <h2 style={{ fontSize: '1.1rem', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '3px', background: color }} />
                    {label}
                </h2>
                <p style={{ color: '#666', fontSize: '0.85rem', marginBottom: '12px' }}>{description}</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {allIds.map(roleId => {
                        const isSelected = selectedIds.includes(roleId);
                        const isStale = staleIds.includes(roleId);
                        const displayName = getRoleName(roleId);
                        return (
                            <button
                                key={roleId}
                                type="button"
                                onClick={() => toggleRoleId(selectedIds, setSelectedIds, roleId)}
                                title={isStale ? 'This role no longer exists in the system. Click to remove.' : undefined}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '4px',
                                    border: isSelected ? `2px solid ${isStale ? '#f59e0b' : color}` : '2px solid #ddd',
                                    background: isSelected ? (isStale ? '#fef3c715' : `${color}15`) : '#fff',
                                    color: isSelected ? (isStale ? '#b45309' : color) : '#555',
                                    fontWeight: isSelected ? 600 : 400,
                                    fontSize: '0.85rem',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s ease',
                                    textDecoration: isStale ? 'line-through' : 'none'
                                }}
                            >
                                {isSelected && <span style={{ marginRight: '6px' }}>{isStale ? '\u26a0' : '\u2713'}</span>}
                                {displayName}
                            </button>
                        );
                    })}
                    {allIds.length === 0 && (
                        <span style={{ color: '#999', fontSize: '0.85rem', fontStyle: 'italic' }}>No roles available</span>
                    )}
                </div>
                {selectedIds.length > 0 && (
                    <div style={{ marginTop: '8px', fontSize: '0.8rem', color: '#888' }}>
                        Selected: {selectedIds.map(id => getRoleName(id)).join(', ')}
                        {staleIds.length > 0 && (
                            <span style={{ color: '#b45309', marginLeft: '8px' }}>
                                ({'\u26a0'} {staleIds.length} legacy role{staleIds.length > 1 ? 's' : ''} {'\u2014'} click to remove)
                            </span>
                        )}
                    </div>
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="sipa-config-page" style={{ padding: '40px', textAlign: 'center' }}>
                <p>Loading workflow configuration...</p>
            </div>
        );
    }

    return (
        <div className="sipa-config-page">
            <h1>SIPA Workflow Role Configuration</h1>
            <p className="page-description">
                Configure which system roles are authorized to perform sign-off actions on SIPA reports.
                Secondary approvers can also perform primary sign-off in someone&apos;s absence (OR logic).
            </p>

            <RoleSelector
                label="Primary Sign-Off Roles"
                description="Roles that can perform the initial (primary) sign-off on SIPA reports. This is typically the Regional Operations Manager or equivalent."
                selectedIds={primaryRoleIds}
                setSelectedIds={setPrimaryRoleIds}
                color="#2563eb"
            />

            <RoleSelector
                label="Secondary Sign-Off Roles (Final Approval)"
                description="Roles that can perform the final (secondary) sign-off. These roles can ALSO perform the primary sign-off when no primary signer is available — enabling one person to complete both stages in someone's absence."
                selectedIds={secondaryRoleIds}
                setSelectedIds={setSecondaryRoleIds}
                color="#059669"
            />

            <RoleSelector
                label="Validation Override Roles"
                description="Roles that can override validation failures (outstanding enforced action items, incomplete scoring) and submit a report even when validation checks are failing. These roles also implicitly have sign-off permission."
                selectedIds={overrideRoleIds}
                setSelectedIds={setOverrideRoleIds}
                color="#dc2626"
            />

            <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'flex-end', 
                padding: '16px 0', 
                borderTop: '1px solid #ddd', 
                marginTop: '8px' 
            }}>
                <div style={{ fontSize: '0.8rem', color: '#888' }}>
                    {lastUpdated && `Last updated: ${new Date(lastUpdated).toLocaleString()}`}
                    {saving && <span style={{ marginLeft: '12px', color: '#2563eb' }}>Saving...</span>}
                </div>
            </div>
        </div>
    );
};

export default SipaWorkflowConfigPage;
